import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Star, CheckCircle2, Sparkles, UserPlus } from "lucide-react";
import { toast } from "sonner";
import {
  betaApply,
  betaMe,
  betaFeedbackStart,
  betaFeedbackMessage,
  betaFeedbackFinish,
} from "@/lib/api";

/**
 * Floating bottom-right feedback widget.
 *
 * Only renders for unlocked beta testers. Clicking the FAB opens a
 * chat-style modal where the Concierge AI conducts a short structured
 * feedback session (tier-aware). On "I'm done" the tester rates 1–5
 * and the session is classified by Claude in the background.
 */
const TIP_HINTS = [
  "Tell us what surprised you (good or bad).",
  "Was anything unclear or broken? Be specific — page + moment.",
  "What's the ONE thing we should change before launch?",
];

export default function BetaFeedback() {
  const [tester, setTester] = useState(null);
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [rating, setRating] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [referrals, setReferrals] = useState([{ name: "", email: "" }]);
  const [referralSending, setReferralSending] = useState(false);
  const [referralsSent, setReferralsSent] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    betaMe()
      .then((m) => {
        if (m?.unlocked) setTester(m);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, completed]);

  const startSession = async () => {
    if (sessionId) return;
    try {
      const data = await betaFeedbackStart(window.location.pathname + window.location.search);
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", content: data.opener }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start the feedback chat.");
      setOpen(false);
    }
  };

  const onToggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (!sessionId && !completed) await startSession();
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    try {
      const data = await betaFeedbackMessage(sessionId, text);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send that. Try again?");
    } finally {
      setSending(false);
    }
  };

  const finishSession = async () => {
    if (!sessionId || finishing) return;
    setFinishing(true);
    try {
      const data = await betaFeedbackFinish(sessionId, rating || null);
      setPromoCode(data.promo_code || "");
      setCompleted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save your feedback. Try again?");
    } finally {
      setFinishing(false);
    }
  };

  if (!tester) return null;

  const updateReferral = (idx, key, value) =>
    setReferrals((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));

  const addReferralRow = () => {
    if (referrals.length >= 3) return;
    setReferrals((prev) => [...prev, { name: "", email: "" }]);
  };

  const submitReferrals = async () => {
    const valid = referrals.filter(
      (r) => r.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim()),
    );
    if (valid.length === 0) {
      toast.error("Add at least one name and a valid email.");
      return;
    }
    setReferralSending(true);
    let sent = 0;
    for (const r of valid) {
      try {
        await betaApply({
          name: r.name.trim(),
          email: r.email.trim(),
          tier: "site_test",
          reason: `Referred by ${tester.name || "an existing tester"}.`,
          source: "referral",
          hp: "",
          elapsed_seconds: 30, // they completed feedback so we know they've dwelled
        });
        sent += 1;
      } catch {
        /* keep going */
      }
    }
    setReferralSending(false);
    setReferralsSent(true);
    toast.success(`${sent} of ${valid.length} referrals sent — thank you.`);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={onToggle}
          data-testid="beta-feedback-fab"
          aria-label="Send feedback"
          className="fixed bottom-24 right-6 z-[7000] bg-[#3A7A8C] hover:bg-[#2c5f6e] text-white px-5 py-3.5 flex items-center gap-2 shadow-lg transition-colors"
        >
          <MessageSquare size={18} />
          <span className="font-semibold text-sm">Feedback</span>
        </button>
      )}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[7100] bg-white border border-[#e5e7eb] shadow-2xl w-[calc(100vw-3rem)] sm:w-[400px] max-w-[400px] h-[600px] max-h-[80vh] flex flex-col"
          data-testid="beta-feedback-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="beta-feedback-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb] bg-[#080808] text-white">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles size={14} className="text-[#9DD4D8] shrink-0" />
              <div className="min-w-0">
                <p
                  id="beta-feedback-title"
                  className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#9DD4D8]"
                >
                  Feedback Concierge
                </p>
                <p className="text-xs text-white/70 truncate">
                  {tester.tier === "full_experience" ? "Full Experience" : "Site"} tester · {tester.name.split(" ")[0]}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close feedback"
              data-testid="beta-feedback-close"
              className="p-1.5 text-white/60 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          {completed ? (
            <div className="flex-1 overflow-y-auto p-6 flex flex-col items-start justify-center text-left">
              <CheckCircle2 size={36} className="text-[#3A7A8C]" />
              <h3 className="mt-4 text-xl font-bold text-[#080808] tracking-tight">
                Thank you — your feedback is logged.
              </h3>
              <p className="mt-3 text-sm text-[#3C4048] leading-relaxed">
                Our team will review every detail before the public launch on 24 August 2026.
              </p>
              {promoCode && (
                <div className="mt-5 bg-[#F5F5F5] border-l-2 border-[#5FA8B4] p-4 w-full">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
                    Your 10% gift voucher
                  </p>
                  <p className="mt-1 text-sm text-[#3C4048]">
                    Quote{" "}
                    <span className="font-mono font-semibold text-[#080808]">{promoCode}</span>{" "}
                    on your next appointment after launch.
                  </p>
                </div>
              )}

              {/* Referral hook */}
              {!referralsSent && (
                <div
                  className="mt-6 w-full border-t border-[#e5e7eb] pt-5"
                  data-testid="beta-referral"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus size={14} className="text-[#3A7A8C]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
                      Know someone else who&apos;d be great?
                    </span>
                  </div>
                  <p className="text-xs text-[#3C4048] leading-relaxed mb-3">
                    Refer up to 3 people. We&apos;ll send them an invite if a spot is still
                    open. Each accepted referral also unlocks a thank-you note from us.
                  </p>
                  <div className="space-y-2">
                    {referrals.map((r, i) => (
                      <div key={i} className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={r.name}
                          onChange={(e) => updateReferral(i, "name", e.target.value)}
                          placeholder="Their name"
                          data-testid={`beta-referral-name-${i}`}
                          className="px-3 py-2 text-xs border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C]"
                        />
                        <input
                          type="email"
                          value={r.email}
                          onChange={(e) => updateReferral(i, "email", e.target.value)}
                          placeholder="Their email"
                          data-testid={`beta-referral-email-${i}`}
                          className="px-3 py-2 text-xs border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C]"
                        />
                      </div>
                    ))}
                    {referrals.length < 3 && (
                      <button
                        type="button"
                        onClick={addReferralRow}
                        data-testid="beta-referral-add"
                        className="text-xs text-[#3A7A8C] hover:underline"
                      >
                        + Add another
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={submitReferrals}
                    disabled={referralSending}
                    data-testid="beta-referral-submit"
                    className="mt-3 ash-btn-secondary text-xs disabled:opacity-60"
                  >
                    {referralSending ? "Sending…" : "Send referral(s)"}
                  </button>
                </div>
              )}
              {referralsSent && (
                <p
                  className="mt-5 text-xs text-[#15803D] font-semibold"
                  data-testid="beta-referral-thanks"
                >
                  Thank you — your referrals are on their way.
                </p>
              )}

              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="beta-feedback-done"
                className="mt-6 ash-btn-secondary text-sm"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#FAFBFC]"
                data-testid="beta-feedback-messages"
              >
                {messages.length === 0 && (
                  <p className="text-sm text-[#6B7280] italic">Starting your feedback chat…</p>
                )}
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-[#3A7A8C] text-white"
                          : "bg-white border border-[#e5e7eb] text-[#080808]"
                      }`}
                      data-testid={`beta-msg-${m.role}-${i}`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <p className="text-xs text-[#6B7280] italic" data-testid="beta-feedback-typing">
                    Concierge is typing…
                  </p>
                )}
              </div>

              {/* Rating + finish */}
              <div className="px-5 pt-3 border-t border-[#e5e7eb] bg-white">
                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#6B7280] mb-2">
                  Overall rating (optional)
                </p>
                <div className="flex items-center gap-1" data-testid="beta-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      aria-label={`Rate ${n} of 5`}
                      data-testid={`beta-rating-${n}`}
                      className="p-1"
                    >
                      <Star
                        size={18}
                        className={n <= rating ? "fill-[#5FA8B4] text-[#5FA8B4]" : "text-[#D1D5DB]"}
                      />
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={finishSession}
                    disabled={finishing}
                    data-testid="beta-feedback-finish"
                    className="ml-auto text-xs font-semibold text-[#3A7A8C] hover:text-[#2c5f6e] disabled:opacity-60"
                  >
                    {finishing ? "Saving…" : "I'm done →"}
                  </button>
                </div>
              </div>

              {/* Input */}
              <form
                onSubmit={sendMessage}
                className="flex items-end gap-2 p-3 border-t border-[#e5e7eb] bg-white"
              >
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={TIP_HINTS[messages.length % TIP_HINTS.length]}
                  rows={2}
                  data-testid="beta-feedback-input"
                  className="flex-1 px-3 py-2 text-sm border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C] resize-none"
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  data-testid="beta-feedback-send"
                  aria-label="Send"
                  className="bg-[#3A7A8C] hover:bg-[#2c5f6e] text-white p-3 disabled:opacity-50"
                >
                  <Send size={16} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
